import { memo, useCallback } from 'react'
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
} from '@mui/material'
import { QuestionnairePerformance, QueryMode } from '../types'
import { formatVersionDateRange } from '../utils/formatVersionDateRange'
import { SxProps, Theme } from '@mui/system'
import { Pagination } from './Pagination'
import { IconName } from '../../../../../../../utils/icons'

interface ResultItemListProps {
  data: QuestionnairePerformance[]
  selectedIds: string[]
  searchValue: string
  onSearchChange: (value: string) => void
  onToggle: (id: string) => void
  onSelectAll: () => void
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  listTitle: string
  styles: {
    questionnaireListPanel: SxProps<Theme>
    questionnaireListHeader: SxProps<Theme>
    questionnaireListSearch: SxProps<Theme>
    questionnaireItem: SxProps<Theme>
    questionnaireItemActive: SxProps<Theme>
    questionnaireItemSelected: SxProps<Theme>
    questionnaireItemText: SxProps<Theme>
    badgeRed: SxProps<Theme>
    badgeYellow: SxProps<Theme>
    badgeGreen: SxProps<Theme>
    paginationContainer: SxProps<Theme>
    paginationButton: SxProps<Theme>
    paginationPage: SxProps<Theme>
    paginationPageActive: SxProps<Theme>
    paginationDisabled: SxProps<Theme>
  }
  colors: { color9: string; color13: string }
  icons: (name: IconName) => React.ReactNode
  t: (key: string, options?: Record<string, unknown>) => string
  queryMode: QueryMode
}

const getScoreStyle = (
  score: number,
  styles: Pick<
    ResultItemListProps['styles'],
    'badgeRed' | 'badgeYellow' | 'badgeGreen'
  >
): SxProps<Theme> => {
  if (score >= 81) return styles.badgeGreen
  if (score >= 61) return styles.badgeYellow
  return styles.badgeRed
}

const ResultItemList = ({
  data,
  selectedIds,
  searchValue,
  onSearchChange,
  onToggle,
  onSelectAll,
  page,
  totalPages,
  onPageChange,
  listTitle,
  styles,
  colors,
  icons,
  t,
  queryMode,
}: ResultItemListProps) => {
  const allSelected = data.length > 0 && data.every((q) => selectedIds.includes(q.id))

  const handleToggleAll = useCallback(() => {
    onSelectAll()
  }, [onSelectAll])

  return (
    <Box sx={styles.questionnaireListPanel}>
      <Box sx={styles.questionnaireListHeader}>
        {listTitle}
      </Box>

      {/* Scrollable list */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {/* Search — sticky arriba */}
        <Box sx={{ ...styles.questionnaireListSearch, position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }}>
          <TextField
            placeholder={t('dashboard.storePerformance.results.search')}
            size="small"
            fullWidth
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {icons('search')}
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Todos — solo si hay más de 1 ítem */}
        {data.length > 1 && (
          <Box
            sx={allSelected ? styles.questionnaireItemActive : styles.questionnaireItem}
            onClick={handleToggleAll}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleToggleAll()
              }
            }}
          >
            <Typography sx={styles.questionnaireItemText}>
              {t('dashboard.storePerformance.results.all')}
            </Typography>
          </Box>
        )}

        {/* Lista de ítems */}
        {data.map((item) => {
          const isSelected = !allSelected && selectedIds.includes(item.id)
          const itemStyle = isSelected
            ? styles.questionnaireItemSelected
            : styles.questionnaireItem
          const { dateText, timeText } = formatVersionDateRange(
            item.firstScheduledDate,
            item.lastScheduledDate,
            t
          )
          return (
            <Box
              key={item.id}
              sx={itemStyle}
              onClick={() => onToggle(item.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onToggle(item.id)
                }
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography sx={styles.questionnaireItemText} title={item.name}>
                  {item.name}
                </Typography>
                {queryMode !== 'many-units-one-questionnaire' && (dateText || timeText) && (
                  <Typography sx={{ fontSize: '11px', color: colors.color9 }}>
                    {[dateText, timeText].filter(Boolean).join(' entre ')}
                  </Typography>
                )}
              </Box>
              <Typography component="span" sx={getScoreStyle(item.score, styles)}>
                {item.score}%
              </Typography>
            </Box>
          )
        })}

        {data.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: colors.color9 }}>
              {t('general.ninformationNotFound')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Paginación */}
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
        maxVisiblePages={2}
        styles={{
          paginationContainer: styles.paginationContainer,
          paginationButton: styles.paginationButton,
          paginationPage: styles.paginationPage,
          paginationPageActive: styles.paginationPageActive,
          paginationDisabled: styles.paginationDisabled,
        }}
      />
    </Box>
  )
}

const ResultItemListMemo = memo(ResultItemList)
export { ResultItemListMemo as ResultItemList }
